import { useMemo } from "react";
import { CATEGORIAS_DE_SERVICO, CATEGORIA_OUTROS, compararCategorias, type AbrangenciaDoServico } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";

const inputCls =
  "h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";
const labelCls = "mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground";

export const NOVA_CATEGORIA = "__nova__";

/** Categoria do serviço — uma das da base, uma que o laboratório já criou, ou uma nova (como a
 *  escala nova nas Cores). `valor` é o nome escolhido ou `NOVA_CATEGORIA`. */
export function CampoCategoria({
  valor,
  novaCategoria,
  onChange,
  onNovaCategoria,
}: {
  valor: string;
  novaCategoria: string;
  onChange: (valor: string) => void;
  onNovaCategoria: (nome: string) => void;
}) {
  const listQuery = trpc.prosthesis.catalog.myServiceCategories.useQuery();
  const opcoes = useMemo(() => {
    const doLab = ((listQuery.data ?? []) as { group: string | null }[]).map((s) => s.group).filter(Boolean) as string[];
    return [...new Set([...CATEGORIAS_DE_SERVICO, ...doLab, CATEGORIA_OUTROS])].sort(compararCategorias);
  }, [listQuery.data]);

  return (
    <div className="space-y-2">
      <div>
        <p className={labelCls}>Categoria</p>
        <select value={valor} onChange={(e) => onChange(e.target.value)} className={inputCls}>
          {opcoes.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
          <option value={NOVA_CATEGORIA}>+ Nova categoria…</option>
        </select>
      </div>
      {valor === NOVA_CATEGORIA && (
        <div>
          <p className={labelCls}>Nome da nova categoria</p>
          <input
            value={novaCategoria}
            onChange={(e) => onNovaCategoria(e.target.value)}
            placeholder="Ex: Trabalhos em PEEK"
            autoFocus
            className={inputCls}
          />
        </div>
      )}
    </div>
  );
}

/** Dente ou arcada: serviço de arcada já chega no pedido com as duas arcadas marcadas. */
export function CampoAbrangencia({
  valor,
  onChange,
}: {
  /** Nulo: configurando vários, e eles estão diferentes — nada marcado até a pessoa escolher. */
  valor: AbrangenciaDoServico | null;
  onChange: (valor: AbrangenciaDoServico) => void;
}) {
  const opcoes: [AbrangenciaDoServico, string][] = [
    ["DENTE", "Por dente"],
    ["ARCADA", "Arcada completa"],
  ];
  return (
    <div>
      <p className={labelCls}>No pedido, o dentista marca</p>
      <div className="flex gap-2">
        {opcoes.map(([chave, rotulo]) => (
          <button
            key={chave}
            type="button"
            onClick={() => onChange(chave)}
            aria-pressed={valor === chave}
            className={cn(
              "h-9 flex-1 rounded-md border text-sm font-medium transition-colors",
              valor === chave
                ? "border-brand-300 bg-brand-50 text-brand-700 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
                : "border-zinc-200 bg-white text-zinc-600 hover:border-brand-300 dark:border-border dark:bg-card dark:text-muted-foreground",
            )}
          >
            {rotulo}
          </button>
        ))}
      </div>
      {valor === null && (
        <p className="mt-1 text-[11px] text-zinc-500 dark:text-muted-foreground">Os selecionados estão diferentes: escolha uma opção para aplicar em todos.</p>
      )}
    </div>
  );
}
